import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RefreshCw, FileSignature, X, Zap, Mic, DownloadCloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { BrandEmblem } from '../BrandEmblem';
import { HealthPulse } from './HealthPulse';
import { StoplightHeaderIndicator } from './EncounterStoplight';
import ConnectionBadge from './ConnectionBadge';
import HeaderMobile from './HeaderMobile';
import HeaderToolbar from './HeaderToolbar';
import HeaderUserMenu from './HeaderUserMenu';
import ReferProviderChip from './ReferProviderChip';
import { ProfileSyncBadge } from './ProfileSyncBadge';
import { MinimalistPill } from './MinimalistPill';
import { MOOD_THEMES } from '../../constants/mediscribe';
import { persistProfilePref } from '../../utils/persistProfilePref';

const API = window.__WR_API_BASE__;

const Header = ({
  textSizeLevel,
  setTextSizeLevel,
  currentMood,
  currentMoodIndex,
  setCurrentMoodIndex,
  favoriteMoods,
  setFavoriteMoods,
  showMoodInfo,
  setShowMoodInfo,
  shareCode,
  setShowShareModal,
  startShareSession,
  startShareHistory,
  historyShareCode,
  isSharing,
  user,
  setShowAuthModal,
  setShowProfileModal,
  setShowTeamSettings,
  setShowProductivityReport,
  myProviders,
  logout,
  userRole,
  switchRole,
  forceSingleColumn,
  toggleColumnLayout,
  readingWidth,
  setReadingWidth,
  readyForSignature,
  onShowSignatureCenter,
  pendingDraftsCount,
  onShowDrafts,
  onShowAllHistory,
  onShowTutorial,
  onShowTeamCollab,
  hasTeam,
  teamUnreadCount = 0,
  autoShareActive = false,
  onShowBatchCharting,
  onShowBugReport,
  onShowShortcuts,
  onShowShipVersion,
  includePatientQuotes,
  setIncludePatientQuotes,
  onNextPatient,
  onGenerateDocs,
  isGenerating,
  notes,
  onClearAll,
  onCopyAll,
  copyAllReady,
  // Feb 27, 2026 — Header was forwarding only onCopyAll; the surgical
  // copy variants (Note-only, Orders-only) and the new Title-only button
  // were being dropped before they reached HeaderToolbar.
  onCopyNoteOnly,
  copyNoteReady,
  onCopyOrdersOnly,
  copyOrdersReady,
  onCopyTitle,
  copyTitleReady,
  // Save button — Feb 24, 2026: surfaced on the top toolbar so provider
  // doesn't have to scroll to the input/output area to save.
  onSaveDraft,
  saveDraftReady,
  onSignAndCopy,
  signAndCopyReady,
  onShowOfflineQueue,
  // Feb 22, 2026: encounter id + sign callback for the in-header stoplight
  // indicator. Provider can see the red/yellow/amber/green state and sign
  // from the toolbar without scrolling back to the output panel.
  currentEncounterId,
  onHeaderSignedToggle,
}) => {
  const navigate = useNavigate();
  const [showSignaturePopover, setShowSignaturePopover] = useState(false);
  const [showDraftsPopover, setShowDraftsPopover] = useState(false);
  const [forceDesktopView, setForceDesktopView] = useState(() => {
    return localStorage.getItem('writeright_desktop_view') === 'true';
  });
  const [isAdmin, setIsAdmin] = useState(false);

  // Pre-compute patient theme indexes so HeaderToolbar doesn't need to import MOOD_THEMES
  const patientThemeIds = useMemo(() => ({
    clinical: MOOD_THEMES.findIndex(m => m.id === 'clinical'),
    calm: MOOD_THEMES.findIndex(m => m.id === 'calm'),
    'high-contrast': MOOD_THEMES.findIndex(m => m.id === 'high-contrast'),
  }), []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) { setIsAdmin(false); return; }
      try {
        const response = await axios.get(`${API}/api/admin/check`, { withCredentials: true });
        setIsAdmin(response.data.is_admin || false);
      } catch { setIsAdmin(false); }
    };
    checkAdmin();
  }, [user]);

  const toggleDesktopView = () => {
    const newValue = !forceDesktopView;
    setForceDesktopView(newValue);
    localStorage.setItem('writeright_desktop_view', newValue.toString());
    persistProfilePref({ force_desktop_view: newValue });
    if (newValue) { document.body.classList.add('force-desktop-view'); }
    else { document.body.classList.remove('force-desktop-view'); }
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', newValue
        ? 'width=1200, initial-scale=0.5, maximum-scale=2'
        : 'width=device-width, initial-scale=1');
    }
    window.dispatchEvent(new Event('resize'));
  };

  useEffect(() => {
    if (forceDesktopView) {
      document.body.classList.add('force-desktop-view');
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) viewport.setAttribute('content', 'width=1200, initial-scale=0.5, maximum-scale=2');
    }
    const handleViewModeChange = (e) => setForceDesktopView(e.detail);
    window.addEventListener('viewmode-changed', handleViewModeChange);
    return () => {
      document.body.classList.remove('force-desktop-view');
      window.removeEventListener('viewmode-changed', handleViewModeChange);
    };
  }, [forceDesktopView]);

  const cycleTextSize = () => setTextSizeLevel((prev) => (prev + 1) % 3);
  const textSizeLabels = ['100%', '150%', '200%'];

  return (
    <>
      <header 
        className="bg-background border-b border-border z-40 transition-all duration-300 fixed top-0 left-0 right-0"
        style={{ position: 'fixed', width: '100%', maxWidth: '100vw' }}
      >
        <div className={`w-full max-w-full overflow-x-hidden px-3 sm:px-6 lg:px-12 xl:px-16 py-1 sm:pt-1.5 ${textSizeLevel > 0 ? 'scale-105 origin-left' : ''}`}>
          {/* Mobile Layout */}
          <HeaderMobile
            textSizeLevel={textSizeLevel}
            setTextSizeLevel={setTextSizeLevel}
            currentMood={currentMood}
            currentMoodIndex={currentMoodIndex}
            setCurrentMoodIndex={setCurrentMoodIndex}
            favoriteMoods={favoriteMoods}
            setFavoriteMoods={setFavoriteMoods}
            setShowMoodInfo={setShowMoodInfo}
            shareCode={shareCode}
            setShowShareModal={setShowShareModal}
            historyShareCode={historyShareCode}
            isSharing={isSharing}
            user={user}
            setShowAuthModal={setShowAuthModal}
            logout={logout}
            userRole={userRole}
            switchRole={switchRole}
            forceSingleColumn={forceSingleColumn}
            toggleColumnLayout={toggleColumnLayout}
            pendingDraftsCount={pendingDraftsCount}
            onShowDrafts={onShowDrafts}
            onShowAllHistory={onShowAllHistory}
            onShowTutorial={onShowTutorial}
            onShowBatchCharting={onShowBatchCharting}
            isAdmin={isAdmin}
            forceDesktopView={forceDesktopView}
            setForceDesktopView={setForceDesktopView}
          />

          {/* Desktop Layout */}
          <div className="hidden sm:block space-y-1">
            {/* Row 1: Logo + Role + Badges + HealthPulse + Auth */}
            <div className="flex items-center justify-between gap-2 relative z-10">
              <div className="flex items-center gap-3">
                <div style={{ '--brand-text-color': 'var(--foreground)' }} data-testid="app-logo" 
                  className="cursor-pointer" onClick={switchRole} title="Return to main page">
                  <BrandEmblem size={textSizeLevel === 2 ? 'md' : 'sm'} />
                </div>
                {userRole && (
                  <button onClick={switchRole}
                    className={`flex flex-col items-center justify-center px-3 py-0.5 text-xs font-bold transition-all active:scale-95 border-2 leading-tight ${
                      userRole === 'provider' 
                        ? 'bg-amber-700 text-white border-amber-300 hover:bg-amber-600' 
                        : 'bg-green-700 text-white border-green-300 hover:bg-green-600'
                    }`}
                    data-testid="role-indicator"
                    title={`Currently ${userRole === 'provider' ? 'Provider' : 'Patient'} Mode - click to switch`}>
                    <span className="text-[11px] font-extrabold tracking-wide">{userRole === 'provider' ? 'Provider' : 'Patient'}</span>
                    <span className="text-[9px] font-medium opacity-90">View</span>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2.5">
                <HealthPulse isAdmin={isAdmin} />

                {/* Always-visible encounter stoplight indicator — Feb 22, 2026.
                    Sits in the header so providers see red/yellow/amber/green
                    + a quick "Sign?" action without scrolling. Auto-hides when
                    no encounter is active. */}
                {userRole === 'provider' && currentEncounterId && (
                  <StoplightHeaderIndicator
                    encounterId={currentEncounterId}
                    onSignedToggle={onHeaderSignedToggle}
                  />
                )}

                {onShowBatchCharting && userRole === 'provider' && (
                  <button onClick={onShowBatchCharting}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-all active:scale-95 bg-amber-600 text-white border-2 border-amber-300 hover:bg-amber-500"
                    data-testid="speed-chart-desktop-btn" title="Speed Chart">
                    <Zap className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Speed</span>
                  </button>
                )}

                {/* Drive-Home Mode launcher — hands-free brain-dump for after-clinic
                    commute. Ships items into the personal To-Do scratchpad
                    automatically; can optionally share each dump to active team. */}
                {userRole === 'provider' && (
                  <a
                    href="/drive"
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-all active:scale-95 bg-purple-700 text-white border-2 border-purple-300 hover:bg-purple-600"
                    data-testid="drive-home-launcher"
                    title="Drive-Home Mode — hands-free brain-dump"
                  >
                    <Mic className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Drive</span>
                  </a>
                )}

                {/* Signature Badge */}
                {userRole === 'provider' && readyForSignature && (readyForSignature.meds > 0 || readyForSignature.profiles > 0) && (
                  <Popover open={showSignaturePopover} onOpenChange={setShowSignaturePopover}>
                    <PopoverTrigger asChild>
                      <Button variant="default" size="sm" className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 rounded-full animate-pulse" data-testid="signature-badge">
                        <FileSignature className="w-3.5 h-3.5" />
                        <Badge variant="secondary" className="ml-1 text-[10px] bg-white/20 text-white">{(readyForSignature.meds || 0) + (readyForSignature.profiles || 0)}</Badge>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 p-0">
                      <div className="p-3 border-b bg-red-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-sm flex items-center gap-2"><FileSignature className="w-4 h-4 text-red-500" />Ready for Signature</h4>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowSignaturePopover(false)}><X className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        {readyForSignature.meds > 0 && (<div className="flex items-center justify-between p-2 bg-amber-500/10 rounded border border-amber-500/30"><span className="text-sm">Medications</span><Badge className="bg-amber-500 text-white">{readyForSignature.meds} ready</Badge></div>)}
                        {readyForSignature.profiles > 0 && (<div className="flex items-center justify-between p-2 bg-orange-500/10 rounded border border-orange-500/30"><span className="text-sm">Temp Profiles</span><Badge className="bg-orange-500 text-white">{readyForSignature.profiles} ready</Badge></div>)}
                        <Button variant="default" size="sm" className="w-full mt-2 bg-red-600 hover:bg-red-700" onClick={() => { setShowSignaturePopover(false); if (onShowSignatureCenter) { onShowSignatureCenter(); setTimeout(() => { const el = document.querySelector('[data-testid="history-card"]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); } }}>View All in History</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {/* Drafts Badge */}
                {userRole === 'provider' && pendingDraftsCount > 0 && (
                  <Popover open={showDraftsPopover} onOpenChange={setShowDraftsPopover}>
                    <PopoverTrigger asChild>
                      <Button variant="default" size="sm" className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="drafts-badge">
                        <FileSignature className="w-3.5 h-3.5" />
                        <Badge variant="secondary" className="ml-1 text-[10px] bg-white/20 text-white">{pendingDraftsCount}</Badge>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 p-0">
                      <div className="p-3 border-b bg-blue-500/10">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-sm flex items-center gap-2"><FileSignature className="w-4 h-4 text-blue-500" />Pending Drafts</h4>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowDraftsPopover(false)}><X className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center justify-between p-2 bg-blue-500/10 rounded border border-blue-500/30"><span className="text-sm">Notes to generate</span><Badge className="bg-blue-500 text-white">{pendingDraftsCount}</Badge></div>
                        <Button variant="default" size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700" onClick={() => { setShowDraftsPopover(false); if (onShowDrafts) { onShowDrafts(); setTimeout(() => { const el = document.querySelector('[data-testid="history-card"]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); } }}>View Drafts</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {/* Update App — Feb 27, 2026 promoted to top-row next to the
                    sign-in / user menu so it lives where the provider goes
                    when they need to fix something. Was previously in the
                    middle sortable cluster crowding the Clear button. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if ('caches' in window) {
                      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
                    }
                    window.location.reload(true);
                  }}
                  className="h-7 px-2 gap-1 text-xs rounded-none bg-slate-700 text-white border-2 border-slate-400 hover:bg-slate-600 shrink-0"
                  data-testid="header-update-btn"
                  title="Update App (clear cache + reload)"
                >
                  <DownloadCloud className="w-3.5 h-3.5" />
                  <span className="text-[10px] hidden lg:inline">Update</span>
                </Button>

                {/* Feb 28, 2026 — always-visible Minimalist View pill so
                    the provider can find it without hunting through Profile
                    Settings or remembering the Ctrl+Shift+M shortcut. User
                    reported "I literally cannot find it on the screen
                    anywhere." */}
                <MinimalistPill />

                {/* Mar 3, 2026 — Refer-a-Provider chip; opens the invite modal.
                    Only renders when signed in. */}
                <ReferProviderChip user={user} compact />

                <HeaderUserMenu
                  user={user}
                  setShowAuthModal={setShowAuthModal}
                  setShowProfileModal={setShowProfileModal}
                  setShowShareModal={setShowShareModal}
                  setShowTeamSettings={setShowTeamSettings}
                  setShowProductivityReport={setShowProductivityReport}
                  logout={logout}
                  isSharing={isSharing}
                  shareCode={shareCode}
                  historyShareCode={historyShareCode}
                  userRole={userRole}
                  isAdmin={isAdmin}
                  includePatientQuotes={includePatientQuotes}
                  setIncludePatientQuotes={setIncludePatientQuotes}
                  onShowShortcuts={onShowShortcuts}
                  onShowTutorial={onShowTutorial}
                  onShowBugReport={onShowBugReport}
                  onShowShipVersion={onShowShipVersion}
                  navigate={navigate}
                />

                {/* Feb 28, 2026 — tiny "✓ Synced across devices" breadcrumb
                    that fades into the header for ~2.2s after any Profile
                    preference saves. Gives the user a visible confirmation
                    that their settings are live across devices so they
                    never wonder again whether a toggle "took". Listens to
                    the `writeright:profile-saved` window event. */}
                <ProfileSyncBadge />
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <HeaderToolbar
              currentMood={currentMood}
              currentMoodIndex={currentMoodIndex}
              setCurrentMoodIndex={setCurrentMoodIndex}
              favoriteMoods={favoriteMoods}
              setFavoriteMoods={setFavoriteMoods}
              setShowMoodInfo={setShowMoodInfo}
              textSizeLevel={textSizeLevel}
              cycleTextSize={cycleTextSize}
              textSizeLabels={textSizeLabels}
              forceDesktopView={forceDesktopView}
              toggleDesktopView={toggleDesktopView}
              forceSingleColumn={forceSingleColumn}
              toggleColumnLayout={toggleColumnLayout}
              readingWidth={readingWidth}
              setReadingWidth={setReadingWidth}
              onShowAllHistory={onShowAllHistory}
              setShowShareModal={setShowShareModal}
              isSharing={isSharing}
              shareCode={shareCode}
              historyShareCode={historyShareCode}
              onShowBatchCharting={onShowBatchCharting}
              userRole={userRole}
              user={user}
              onShowTeamCollab={onShowTeamCollab}
              autoShareActive={autoShareActive}
              teamUnreadCount={teamUnreadCount}
              onNextPatient={onNextPatient}
              onGenerateDocs={onGenerateDocs}
              isGenerating={isGenerating}
              notes={notes}
              onClearAll={onClearAll}
              onCopyAll={onCopyAll}
              copyAllReady={copyAllReady}
              onCopyNoteOnly={onCopyNoteOnly}
              copyNoteReady={copyNoteReady}
              onCopyOrdersOnly={onCopyOrdersOnly}
              copyOrdersReady={copyOrdersReady}
              onCopyTitle={onCopyTitle}
              copyTitleReady={copyTitleReady}
              onSaveDraft={onSaveDraft}
              saveDraftReady={saveDraftReady}
              onSignAndCopy={onSignAndCopy}
              signAndCopyReady={signAndCopyReady}
              patientThemeIds={patientThemeIds}
              onShowOfflineQueue={onShowOfflineQueue}
            />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
;
